/**
 * Sage AI - Strategic Business Intelligence Assistant
 * For Admins and Managers only
 * Capabilities: Content Creation, Analytics, Pricing, Social Media, Registration
 */
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { MessageCircle, Send, Brain, X, Minimize2, Maximize2, Loader2, Copy, CheckCircle, Sparkles, TrendingUp, FileText, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { BACKEND_URL } from '@/lib/backendUrl';


export const SageChat = ({ 
  hotelId, 
  currentUser,
  isOpen = false,
  onToggle
}) => {
  
  // Get language from context
  const { language } = useLanguage();
  
  const getWelcomeMessage = () => {
    return `🧠 Welcome ${currentUser?.name || 'there'}! I'm Sage, your strategic business advisor.

I can help you with:
📝 **Content Creation**
• Write compelling hotel descriptions
• Create room type details
• Highlight amenities & features

📊 **Analytics & Insights**
• Performance reports
• Revenue optimization
• Occupancy trends

💰 **Pricing Intelligence**
• Dynamic rate suggestions
• Discount optimization
• Market comparisons

📱 **Social Media Marketing**
• Create engaging posts
• Campaign ideas
• Hashtag strategies

🏨 **Hotel Registration** (Coming soon)
• Guided setup wizard

What would you like help with today?`;
  };
  
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: getWelcomeMessage(),
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Quick action buttons for common tasks
  const quickActions = [
    { label: 'Write Hotel Description', icon: FileText, prompt: 'Write a compelling description for my hotel' },
    { label: 'Social Media Post', icon: Share2, prompt: 'Create a social media post for Instagram' },
    { label: 'Analyze Performance', icon: TrendingUp, prompt: 'Show me my hotel performance analytics' },
    { label: 'Optimize Pricing', icon: Sparkles, prompt: 'Suggest optimal room rates for this week' }
  ];

  const handleSend = async (messageText = null) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || loading) return;

    // Clear input
    setInput('');

    // Add user message
    setMessages(prev => [...prev, {
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    }]);

    setLoading(true);

    try {
      // Call Sage AI API
      const response = await fetch(`${BACKEND_URL}/api/sage/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId || 'unknown',
          user_id: currentUser?.id || currentUser?.email || 'unknown',
          user_role: currentUser?.role || 'admin',
          message: textToSend,
          conversation_history: messages.slice(-5), // Last 5 messages for context
          language: language || 'en' // Pass selected language
        })
      });

      const data = await response.json();

      if (data.success) {
        // Add Sage's response
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          action: data.action, // e.g., "content_created", "analytics_generated"
          metadata: data.metadata // Additional data like generated content, stats, etc.
        };
        
        setMessages(prev => [...prev, assistantMessage]);

        // Show success toast for actions
        if (data.action === 'content_created') {
          toast.success('✨ Content generated successfully!');
        } else if (data.action === 'analytics_generated') {
          toast.success('📊 Analytics report ready!');
        }
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Sage AI error:', error);
      const errorMessage = {
        role: 'assistant',
        content: '⚠️ I encountered an error. Please try again or contact support if this persists.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to communicate with Sage AI');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard!');
    }).catch(() => {
      toast.info('Text ready to copy', { description: text.substring(0, 100) + '...' });
    });
  };

  if (!isOpen) {
    return (
      <Button
        onClick={onToggle}
        className="fixed bottom-6 right-24 w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 z-50"
        data-testid="sage-chat-button"
        title="Sage AI - Business Intelligence"
      >
        <div className="relative">
          <Brain className="w-7 h-7 text-white" />
          <Sparkles className="w-4 h-4 text-yellow-300 absolute -top-1 -right-1 animate-pulse" />
        </div>
      </Button>
    );
  }

  return (
    <Card className={`fixed bottom-6 right-24 shadow-2xl border-2 border-purple-200 z-50 transition-all duration-300 ${
      isMinimized ? 'w-80 h-16' : 'w-[480px] h-[680px]'
    }`}>
      {/* Header */}
      <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 cursor-pointer" onClick={() => setIsMinimized(!isMinimized)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <Brain className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                Sage AI
                <Badge variant="secondary" className="text-[10px] bg-yellow-400 text-purple-900 border-0">GURU</Badge>
              </h3>
              <p className="text-xs text-purple-100">Strategic Business Intelligence</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(!isMinimized);
              }}
              className="text-white hover:bg-white/20 h-8 w-8 p-0"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="text-white hover:bg-white/20 h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Chat Messages */}
      {!isMinimized && (
        <>
          <CardContent className="p-4 h-[480px] overflow-y-auto bg-gradient-to-b from-purple-50 to-white">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                        : msg.isError
                        ? 'bg-red-100 text-red-800 border border-red-300'
                        : 'bg-white text-gray-900 shadow-md border border-purple-100'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className={`text-xs ${
                        msg.role === 'user' ? 'text-purple-100' : 'text-gray-400'
                      }`}>
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {msg.role === 'assistant' && !msg.isError && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(msg.content)}
                          className="h-6 px-2 text-xs hover:bg-purple-100"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-2xl px-4 py-3 shadow-md border border-purple-100">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                      <span className="text-sm text-gray-600">Sage is thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </CardContent>

          {/* Input Area */}
          <div className="p-4 border-t bg-white">
            {/* Quick Actions */}
            <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
              {quickActions.map((action, idx) => {
                const Icon = action.icon;
                return (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="cursor-pointer hover:bg-purple-100 hover:border-purple-400 text-xs whitespace-nowrap transition-colors"
                    onClick={() => setInput(action.prompt)}
                  >
                    <Icon className="w-3 h-3 mr-1" />
                    {action.label}
                  </Badge>
                );
              })}
            </div>
            
            <div className="flex gap-2">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Sage anything about your hotel business..."
                className="flex-1 min-h-[44px] max-h-[120px] resize-none"
                disabled={loading}
                data-testid="sage-input"
                rows={1}
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="h-[44px] px-6 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                data-testid="sage-send-button"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
